import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Container,
} from "@mui/material";

import axios from "axios";
import config from "../../config.json";
import storeItems from "../../data/storeItems";
import AuthService from "../../services/authService";

const StorePage = () => {
    const [selectedCategory, setSelectedCategory] = useState("All");
    const [selectedItem, setSelectedItem] = useState(null);
    const [loading, setLoading] = useState(false);
    const [refundAccepted, setRefundAccepted] = useState(false);

    const [playerUsername, setPlayerUsername] = useState(null);
    const [characterName, setCharacterName] = useState(null);
    const [playerEmail, setPlayerEmail] = useState(null);
    const [currentFunds, setCurrentFunds] = useState(null);
    const [onlineStatus, setOnlineStatus] = useState(null);

    const categories = [
        "All",
        "Featured",
        "SpecialGarment",
        "MiraculousGourd",
        "DBScroll",
        "VIP 6",
        "Misc Items",
    ];

    const createPaypalOrderEndpoint =
        config.apiEndPoint + "paypal/create-order";

    const createPaymentwallWidgetEndpoint =
        config.apiEndPoint + "paymentwall/create-widget";

    const supportEmail = "VexConquer@gmail.com";

    useEffect(() => {
        const token = AuthService.getJwt();
        if (!token) return;

        axios
            .get(config.apiEndPoint + "wallet/current-funds", {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => {
                setPlayerUsername(res.data.username ?? null);
                setPlayerEmail(res.data.email ?? null);
                setCurrentFunds(res.data.balance ?? null);
                setOnlineStatus(res.data.online ?? null);
                setCharacterName(res.data.characterName ?? null);
            })
            .catch((err) => console.log(err));
    }, []);

    const filteredItems =
        selectedCategory === "All"
            ? storeItems
            : storeItems.filter((i) => i.category === selectedCategory);

    const handleAcceptRefundPolicy = () => {
        setRefundAccepted(true);
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                background:
                    "linear-gradient(to bottom, #050505 0%, #0b0b0b 100%)",
                position: "relative",
                overflow: "hidden",
                py: 10,
            }}
        >
            {/* GOLD GLOW */}
            <Box
                sx={{
                    position: "absolute",
                    top: -200,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 600,
                    height: 600,
                    borderRadius: "50%",
                    background:
                        "radial-gradient(circle, rgba(212,164,55,0.18) 0%, transparent 70%)",
                    filter: "blur(40px)",
                }}
            />

            <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>

                {/* HEADER */}
                <Box sx={{ textAlign: "center", mb: 6 }}>
                    <Typography
                        sx={{
                            color: "#D4A437",
                            fontWeight: 900,
                            fontSize: { xs: "38px", md: "64px" },
                            fontFamily: "serif",
                            letterSpacing: "2px",
                            textShadow: "0 0 25px rgba(212,164,55,0.35)",
                        }}
                    >
                        Vex Store
                    </Typography>

                    <Typography
                        sx={{
                            color: "rgba(255,255,255,0.7)",
                            maxWidth: 800,
                            mx: "auto",
                            mt: 2,
                            lineHeight: "32px",
                        }}
                    >
                        Premium items, VIP packs, and exclusive rewards to enhance your journey in Vex Conquer.
                    </Typography>
                </Box>

                {/* ACCOUNT CARD */}
                <Box
                    sx={{
                        mb: 6,
                        border: "1px solid rgba(212,164,55,0.25)",
                        borderRadius: "16px",
                        background:
                            "linear-gradient(to bottom, rgba(18,12,7,0.96), rgba(5,5,5,1))",
                        p: 4,
                    }}
                >
                    <Typography sx={{ color: "#D4A437", fontWeight: 800, mb: 2 }}>
                        Account Overview
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Typography color="#fff">User: {playerUsername || "Guest"}</Typography>
                            <Typography color="#fff">Character: {characterName || "None"}</Typography>
                            <Typography color="#fff">Email: {playerEmail || "None"}</Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography
                                sx={{
                                    color:
                                        onlineStatus === "1" ? "#45d15f" : "#ff4b4b",
                                    fontWeight: 700,
                                }}
                            >
                                Status: {onlineStatus === "1" ? "ONLINE" : "OFFLINE"}
                            </Typography>

                            <Typography sx={{ color: "#D4A437", fontWeight: 800 }}>
                                Funds: ${Number(currentFunds || 0).toFixed(2)}
                            </Typography>

                            <Typography sx={{ color: "rgba(255,255,255,0.6)", mt: 1 }}>
                                Support: {supportEmail}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                {/* CATEGORY FILTER */}
                <Box sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 2, mb: 6 }}>
                    {categories.map((cat) => (
                        <Button
                            key={cat}
                            onClick={() => setSelectedCategory(cat)}
                            sx={{
                                border: "1px solid rgba(212,164,55,0.3)",
                                color: selectedCategory === cat ? "#111" : "#D4A437",
                                background: selectedCategory === cat ? "#D4A437" : "transparent",
                                fontWeight: 700,
                                px: 3,
                                "&:hover": {
                                    background: "#D4A437",
                                    color: "#111",
                                },
                            }}
                        >
                            {cat}
                        </Button>
                    ))}
                </Box>

                {/* ITEMS */}
                <Grid container spacing={4}>
                    {filteredItems.map((item) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                            <Card
                                sx={{
                                    background:
                                        "linear-gradient(to bottom, rgba(18,12,7,0.96), rgba(5,5,5,1))",
                                    border: "1px solid rgba(212,164,55,0.2)",
                                    borderRadius: "14px",
                                    transition: "0.3s",
                                    "&:hover": {
                                        transform: "translateY(-6px)",
                                        border: "1px solid rgba(212,164,55,0.5)",
                                    },
                                }}
                            >
                                <Box sx={{ height: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <img src={item.image} alt={item.name} style={{ maxHeight: "100%" }} />
                                </Box>

                                <CardContent sx={{ textAlign: "center" }}>
                                    <Typography sx={{ color: "#D4A437", fontWeight: 800 }}>
                                        {item.name}
                                    </Typography>

                                    <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: 13, minHeight: 60 }}>
                                        {item.description}
                                    </Typography>

                                    <Typography sx={{ color: "#fff", fontWeight: 800, my: 2 }}>
                                        ${item.price}
                                    </Typography>

                                    <Button
                                        fullWidth
                                        onClick={() => setSelectedItem(item)}
                                        sx={{
                                            background:
                                                "linear-gradient(to bottom, #d4a437, #8f6517)",
                                            color: "#111",
                                            fontWeight: 800,
                                        }}
                                    >
                                        BUY NOW
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* DIALOG */}
                <Dialog open={!!selectedItem} onClose={() => setSelectedItem(null)}>
                    <DialogTitle sx={{ background: "#1a1208", color: "#D4A437" }}>
                        Confirm Purchase
                    </DialogTitle>

                    <DialogContent sx={{ background: "#0b0b0b", color: "#fff", py: 3 }}>
                        Buy <b style={{ color: "#D4A437" }}>{selectedItem?.name}</b>?
                    </DialogContent>

                    <DialogActions sx={{ background: "#0b0b0b" }}>
                        <Button onClick={() => setSelectedItem(null)} sx={{ color: "#fff" }}>
                            Cancel
                        </Button>

                        <Button
                            href="https://discord.gg/GusbbTUdRQ"
                            target="_blank"
                            sx={{
                                background: "#b71c1c",
                                color: "#fff",
                                fontWeight: 700,
                            }}
                        >
                            Contact GM (Payments Closed)
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Box>
    );
};

export default StorePage;